import { useState, useMemo, useEffect } from "react";
import { Form, Input, Button, Spinner } from "@heroui/react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { faEyeSlash, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import axiosInstance from "../../../shared/api/axiosInstance";
import FormAlert from "../../../shared/components/form-alert/FormAlert";
import {
  passwordRequirements,
  validatePassword,
  validatePasswordMatch,
} from "../../invites/utils/passwordValidation";
import { useAuth } from "../hooks/useAuth";
import { TLoggedInUserInfo } from "../../../shared/types/loggedInUserInfo";
import { trackException, trackInfo, LogLevel } from "../../../shared/services/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../../../shared/services/appInsightsConstants";

type MessageType = "success" | "danger" | "warning" | "info";

interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const ChangePassword = () => {
  const loggedInUserInfo = useOutletContext<TLoggedInUserInfo>();
  const { isAuthenticated, isLoading } = useAuth();
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<MessageType | null>(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<ChangePasswordFormData>({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [hasInteracted, setHasInteracted] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });
  const navigate = useNavigate();

  // Authentication check
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/account/login", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Real-time password validation for new password
  const newPasswordValidation = useMemo(() => {
    if (!form.newPassword) return { isValid: false, errors: [] };
    return validatePassword(form.newPassword);
  }, [form.newPassword]);

  const passwordMatchValidation = useMemo(() => {
    if (!form.confirmNewPassword) return { isValid: true };
    return validatePasswordMatch(form.newPassword, form.confirmNewPassword);
  }, [form.newPassword, form.confirmNewPassword]);

  const isValid =
    form.currentPassword &&
    form.newPassword &&
    form.confirmNewPassword &&
    newPasswordValidation.isValid &&
    passwordMatchValidation.isValid &&
    form.currentPassword !== form.newPassword; // Ensure new password is different

  const handleInputChange = (
    field: keyof ChangePasswordFormData,
    value: string
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setHasInteracted((prev) => ({ ...prev, [field]: true }));
    // Clear messages when user starts typing
    if (message) {
      setMessage(null);
      setMessageType(null);
    }
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setLoading(true);
    setMessage(null);
    setMessageType(null);

    // Track password change attempt
    trackInfo("Password change attempt", { 
      userId: loggedInUserInfo.id,
      emailAddress: loggedInUserInfo.emailAddress,
      component: AppInsightsComponents.CHANGE_PASSWORD,
      action: AppInsightsActions.PASSWORD_CHANGE_ATTEMPT
    });

    try {
      const response = await axiosInstance.put(
        "/account/ChangeUserPassword",
        {
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
          id: loggedInUserInfo.id,
        },
        { withCredentials: true }
      );
      if (response.status === 200) {
        // Track successful password change
        trackInfo("Password change successful", { 
          userId: loggedInUserInfo.id,
          emailAddress: loggedInUserInfo.emailAddress,
          component: AppInsightsComponents.CHANGE_PASSWORD,
          action: AppInsightsActions.PASSWORD_CHANGE_SUCCESS
        });

        setMessage(
          "Password changed successfully! You will be redirected to the product selection page."
        );
        setMessageType("success");

        // Reset form
        setForm({
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        });
        setHasInteracted({
          currentPassword: false,
          newPassword: false,
          confirmNewPassword: false,
        });

        // Redirect after 3 seconds
        setTimeout(() => {
          navigate("/product-selection", { replace: true });
        }, 3000);
      }
    } catch (error) {
      // Track password change failure
      trackException(
        error as Error,
        LogLevel.Error,
        { 
          userId: loggedInUserInfo.id,
          emailAddress: loggedInUserInfo.emailAddress,
          component: AppInsightsComponents.CHANGE_PASSWORD,
          action: AppInsightsActions.PASSWORD_CHANGE_FAILURE,
          errorType: axios.isAxiosError(error) ? AppInsightsErrorTypes.API_ERROR : AppInsightsErrorTypes.UNEXPECTED_ERROR,
          statusCode: axios.isAxiosError(error) ? (error.response?.status?.toString() || "unknown") : "unknown"
        }
      );

      if (axios.isAxiosError(error) && error.response?.data) {
        setMessage(
          error.response.data.message ||
            "Error changing password. Please try again."
        );
        setMessageType("danger");
      } else {
        setMessage(
          "An unexpected error occurred. Please try again later or contact MSI."
        );
        setMessageType("danger");
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="form col-span-6 md:col-span-3 lg:col-span-4 flex items-center justify-center min-h-screen py-8">
        <div className="flex flex-col items-center justify-center mx-auto space-y-4">
          <Spinner
            size="lg"
            label="Checking authentication..."
            classNames={{ label: "text-md" }}
            variant="simple"
          />
          <p className="text-gray-600">Please wait...</p>
        </div>
      </div>
    );
  }

  // Don't render the form if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="form col-span-6 md:col-span-3 lg:col-span-4">
      <div className="grid grid-cols-8">
        <div className="col-span-3 sm:col-span-3 md:col-span-3 lg:col-span-2 xl:col-span-2 2xl:col-span-2">
          <h6 className="whitespace-nowrap">Change Password</h6>
        </div>
        <div className="m-1.5 col-span-8 sm:col-span-8 md:col-span-8 lg:col-span-5 xl:col-span-4 2xl:col-span-3">
          <Form onSubmit={handleSubmit}>
            {/* Current Password */}
            <Input
              isRequired
              errorMessage={
                form.currentPassword === "" && hasInteracted.currentPassword
                  ? "Current password is required"
                  : undefined
              }
              isInvalid={
                form.currentPassword === "" && hasInteracted.currentPassword
              }
              label="Current Password"
              labelPlacement="outside"
              name="currentPassword"
              placeholder="Enter your current password"
              type={showPasswords.current ? "text" : "password"}
              value={form.currentPassword}
              classNames={{
                inputWrapper: "rounded-none border-1",
                label: "label",
              }}
              onChange={(e) =>
                handleInputChange("currentPassword", e.target.value)
              }
              disabled={loading}
              endContent={
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("current")}
                  className="absolute inset-y-0 right-0 px-4 flex items-center text-sm text-gray-600 hover:text-gray-800"
                  disabled={loading}
                >
                  <FontAwesomeIcon
                    icon={showPasswords.current ? faEyeSlash : faEye}
                  />
                </button>
              }
            />

            {/* New Password */}
            <Input
              isRequired
              errorMessage={
                form.newPassword === "" && hasInteracted.newPassword
                  ? "New password is required"
                  : form.newPassword !== "" && !newPasswordValidation.isValid
                  ? "Please meet all password requirements"
                  : form.newPassword &&
                    form.currentPassword === form.newPassword
                  ? "New password must be different from current password"
                  : undefined
              }
              isInvalid={
                (!newPasswordValidation.isValid && form.newPassword !== "") ||
                (form.newPassword === "" && hasInteracted.newPassword) ||
                (form.newPassword !== "" &&
                  form.currentPassword === form.newPassword)
              }
              label="New Password"
              labelPlacement="outside"
              name="newPassword"
              placeholder="Enter your new password"
              type={showPasswords.new ? "text" : "password"}
              value={form.newPassword}
              classNames={{
                inputWrapper: "rounded-none border-1",
                label: "label",
              }}
              onChange={(e) => handleInputChange("newPassword", e.target.value)}
              disabled={loading}
              endContent={
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("new")}
                  className="absolute inset-y-0 right-0 px-4 flex items-center text-sm text-gray-600 hover:text-gray-800"
                  disabled={loading}
                >
                  <FontAwesomeIcon
                    icon={showPasswords.new ? faEyeSlash : faEye}
                  />
                </button>
              }
            />

            {/* Password Requirements */}
            {form.newPassword && (
              <div className="mt-1 mb-2 space-y-2">
                {passwordRequirements.map((requirement, index) => {
                  const isRequirementValid = requirement.test(form.newPassword);
                  return (
                    <div key={index} className="flex items-center text-sm">
                      <div
                        className={`w-3 h-3 rounded-full mr-3 ${
                          isRequirementValid ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                      <span
                        className={
                          isRequirementValid ? "text-green-600" : "text-red-600"
                        }
                      >
                        {requirement.message}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Confirm New Password */}
            <Input
              isRequired
              errorMessage={
                form.confirmNewPassword === "" &&
                hasInteracted.confirmNewPassword
                  ? "Confirm new password is required"
                  : form.confirmNewPassword !== "" &&
                    !passwordMatchValidation.isValid
                  ? "Passwords do not match"
                  : !newPasswordValidation.isValid && hasInteracted.newPassword
                  ? "Please meet password requirements first"
                  : undefined
              }
              isInvalid={
                (!passwordMatchValidation.isValid &&
                  form.confirmNewPassword !== "") ||
                (form.confirmNewPassword === "" &&
                  hasInteracted.confirmNewPassword) ||
                (!newPasswordValidation.isValid && hasInteracted.newPassword)
              }
              label="Confirm New Password"
              labelPlacement="outside"
              name="confirmNewPassword"
              placeholder="Re-enter your new password"
              type={showPasswords.confirm ? "text" : "password"}
              value={form.confirmNewPassword}
              classNames={{
                inputWrapper: "rounded-none border-1",
                label: "label",
              }}
              onChange={(e) =>
                handleInputChange("confirmNewPassword", e.target.value)
              }
              disabled={loading}
              endContent={
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("confirm")}
                  className="absolute inset-y-0 right-0 px-4 flex items-center text-sm text-gray-600 hover:text-gray-800"
                  disabled={loading}
                >
                  <FontAwesomeIcon
                    icon={showPasswords.confirm ? faEyeSlash : faEye}
                  />
                </button>
              }
            />

            {/* Password Match Validation */}
            {form.confirmNewPassword && (
              <div className="mt-0 flex items-center text-sm">
                <div
                  className={`w-3 h-3 rounded-full mr-3 ${
                    passwordMatchValidation.isValid
                      ? "bg-green-500"
                      : "bg-red-500"
                  }`}
                />
                <span
                  className={
                    passwordMatchValidation.isValid
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  {passwordMatchValidation.isValid
                    ? "Passwords match"
                    : passwordMatchValidation.error}
                </span>
              </div>
            )}

            {message && messageType && (
              <FormAlert message={message} messageType={messageType} />
            )}

            <div className="flex w-full gap-4 items-center justify-between mt-4">
              <div className="space-x-4">
                <a
                  className="navigate-link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/product-selection");
                  }}
                >
                  Cancel
                </a>
                <a
                  className="navigate-link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/account/forgot-password");
                  }}
                >
                  Forgot password?
                </a>
              </div>
              <Button
                isDisabled={!isValid || loading}
                size="md"
                className="rounded-none"
                color="primary"
                type="submit"
                title={
                  !isValid && !loading
                    ? "Please fill all fields and meet password requirements"
                    : ""
                }
              >
                {loading ? "Changing Password..." : "Change Password"}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
